import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CDCHeader, CDCBody, CDCFooter } from "@cdcent/templatepackage-react";
import "@cdcent/templatepackage-react/assets/css/bootstrap.min.css";
import "@cdcent/templatepackage-react/assets/css/app.min.css";
import "./scss/main.scss";
import Dashboard from "./Components/Dashboard";
import DataVisualization from "./Components/DataVisualization";
import LoadingOverlay from "./Components/LoadingOverlay";
import * as ENV from "./Util/env";
import {
  selectTopic,
  selectSubTopic,
  selectSubTopicAll,
  selectCdcHeader,
  selectCdcHelpLink,
} from "./Util/Analytics/TopicAnalytics";
import { Routes, Route } from "react-router-dom";

import { GetQuestions, GetTopics, GetLocations } from "./Util/Api";
import ScrollToTop from "./Util/ScrollToTop";
import { DEFAULT_YEAR } from "./Util/constants";
const App = () => {
  let navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [questionId, setQuestionId] = useState(null);
  const [selectedData, setSelectedData] = useState(null);
  const [topicId, setTopicId] = useState(null);
  const [question, setQuestion] = useState(null);
  const [year, setYear] = useState(null);
  const [vizType, setVizType] = useState("null");
  // Info banner message for user notifications (e.g., location reset for map view)
  const [infoBannerMessage, setInfoBannerMessage] = useState(null);
  const [displayLocations, setDisplayLocations] = useState(false);
  // Global sample size preferences (persist across questions and navigation)
  const [showSampleSize, setShowSampleSize] = useState(() => {
    try {
      const stored = window.localStorage.getItem("yrbs_showSampleSize");
      return stored === "true";
    } catch (e) {
      return false;
    }
  });
  // Track if a location was chosen via dropdown (not currently used on dashboard)
  const setLocationSelectedFromDropdownMenu = () => {}; // no-op placeholder for dashboard usage

  const defaultLocation = {
    LocationAbbr: "ALL",
    LocationDesc: "All Locations",
    LocationType: "ALL",
  };
  const [location, setLocation] = useState(defaultLocation);
  const [locations, setLocations] = useState([]);
  // Only set subQuestionValue from URL param, not auto-select first subquestion.
  // This ensures View #2 shows correctly when no subQuestionId is in the URL.
  // See docs/view-determination-rules.md for view logic details.
  const [subQuestionValue, setSubQuestionValue] = useState(null);

  const [errorLoadingData, setErrorLoadingData] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const questionCodeParam = searchParams.get("questionId");
  const subQuestionCodeParam = searchParams.get("subQuestionId");
  // Parse year as a number since the API returns years as numbers (e.g., { Year: 2024 })
  // and the dropdown compares by strict equality
  const yearCodeParamRaw = searchParams.get("year");
  const yearCodeParam = yearCodeParamRaw ? parseInt(yearCodeParamRaw, 10) : null;
  const locationParam = searchParams.get("LocationAbbr");
  const topicIdParam = searchParams.get("topicId");
  const subTopicIdParam = searchParams.get("subTopicId");

  const [topics, setTopics] = useState([]);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    pageInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hydrate topic/subtopic filters from URL params whenever they change
  // This ensures that when returning to Home with topicId/subTopicId in the URL,
  // the Topic Selection widget displays the previously selected filters.
  useEffect(() => {
    // Only sync topic/subtopic from URL when on the dashboard (no questionId selected)
    // to avoid interference with visualization state
    if (questionCodeParam) return;

    // When topicIdParam is present in URL, set it; when absent, clear it
    if (topicIdParam) {
      setTopicId(topicIdParam);
    } else if (topicId !== null) {
      // Only clear if we currently have a value to avoid unnecessary re-renders
      setTopicId(null);
    }

    // When subTopicIdParam is present in URL, set it; when absent, clear it
    if (subTopicIdParam) {
      setSubTopicId(subTopicIdParam);
    } else if (subTopicId !== null) {
      // Only clear if we currently have a value to avoid unnecessary re-renders
      setSubTopicId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicIdParam, subTopicIdParam, questionCodeParam]);

  // Hydrate location filter from URL params when on the dashboard
  // This ensures that when returning to Home with LocationAbbr in the URL,
  // the Location dropdown displays the previously selected location.
  useEffect(() => {
    // Only sync location from URL when on the dashboard (no questionId selected)
    // to avoid interference with visualization state
    if (questionCodeParam) return;
    if (!locations || locations.length === 0) return;

    if (locationParam) {
      const loc = locations.find((x) => x.LocationAbbr === locationParam);
      if (loc && (!location || location.LocationAbbr !== locationParam)) {
        setLocation(loc);
      }
    } else if (location && location.LocationAbbr !== "ALL") {
      // When locationParam is absent and we're on dashboard, reset to ALL
      setLocation(defaultLocation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParam, questionCodeParam, locations]);

  // React to URL param changes after initial load (HashRouter updates searchParams).
  // Only hydrate visualization state when the hash path actually points to a viz route
  // (e.g., #/table, #/map, #/graph). This prevents an explicit return to the
  // dashboard (no viz route in hash) from being "pulled" back into a table view
  // just because a questionId may still be present in some edge cases.
  // Track if we're in the middle of a programmatic year change to avoid
  // the hydration effect overwriting state during handleYearChange.
  const isYearChangeInProgress = React.useRef(false);
  
  useEffect(() => {
    (async () => {
      // Skip if we're in the middle of a year change - handleYearChange will handle state
      if (isYearChangeInProgress.current) return;
      
      // Only proceed when base datasets are available
      if (!locations || locations.length === 0) return;

      // Derive current viz from hash path (e.g., #/table/?...)
      const full = (window?.location?.hash || "").replace("#/", "");
      const base = (full.split("/")[0] || "");

      // If the hash does not point at a known viz route, stay on dashboard.
      // This is important when the user has clicked Home: we do not want to
      // re-hydrate a prior /table view unless the URL explicitly requests it.
      const allowedViz = ["table", "map", "graph"]; 
      if (!base || !allowedViz.includes(base)) {
        return;
      }

      // If no questionId is present, do not override dashboard state here
      if (!questionCodeParam) return;

      const locAbbr = locationParam || location?.LocationAbbr || "ALL";
      const loc = locations.find((x) => x.LocationAbbr === locAbbr) || defaultLocation;
      const yr = yearCodeParam || DEFAULT_YEAR;

      // Ensure we have a question set that includes the requested id; refetch if missing.
      // When a subQuestionId is in the URL, also filter by year to get the correct SubQuestions.
      let q = (questions || []).find((x) => x.QuestionId === questionCodeParam) || null;
      
      // If we have a subQuestionId in URL, we need to refetch questions with the year filter
      // to ensure the SubQuestions array contains entries for that year
      const needsYearFilteredRefetch = subQuestionCodeParam && q && !(
        (q.SubQuestions || []).some((s) => String(s.SubQuestionId).toLowerCase() === String(subQuestionCodeParam).toLowerCase())
      );
      
      if (!q || needsYearFilteredRefetch) {
        try {
          const refreshed = await fetchQuestions({ LocationAbbr: locAbbr, filteredYear: yr });
          if (Array.isArray(refreshed) && refreshed.length) {
            setQuestions(refreshed);
            q = refreshed.find((x) => x.QuestionId === questionCodeParam) || q;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error("Failed to refresh questions for URL sync", e);
        }
      }
      const subq = subQuestionCodeParam || null;

      // If nothing actually changed, skip updates
      // Also check if selectedData already has the right subQuestion to avoid overwriting
      // state that handleYearChange has already set correctly.
      const currentSubQuestion = selectedData?.subQuestion;
      const noChange = (
        question?.QuestionId === q?.QuestionId &&
        location?.LocationAbbr === loc?.LocationAbbr &&
        String(year) === String(yr) &&
        String(subQuestionValue || "") === String(subq || "") &&
        vizType === base
      );
      
      // Also skip if we already have the correct subQuestion resolved
      const subQuestionAlreadyResolved = subq && currentSubQuestion && 
        String(currentSubQuestion.SubQuestionId).toLowerCase() === String(subq).toLowerCase();
      
      if (noChange || subQuestionAlreadyResolved) return;

      setVizType(base);
      setQuestion(q);
      setQuestionId(q ? q.QuestionId : null);
      setLocation(loc);
      setYear(yr);
      setSubQuestionValue(subq);

      // Also set selectedData when we have a question context
      if (q) {
        setSelectedData({
          vizType: base,
          question: q,
          questionId: q.QuestionId,
          subQuestionValue: subq,
          subQuestion: subq
            ? (q.SubQuestions || []).find((s) => String(s.SubQuestionId).toLowerCase() === String(subq).toLowerCase()) || null
            : null,
          year: yr,
          location: loc,
          // Don't scroll on URL hydration - user didn't explicitly click a subquestion
          scrollToTableHeader: false,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionCodeParam, subQuestionCodeParam, yearCodeParam, locationParam, questions, locations]);

  async function pageInit() {
    await setDataLoading(true);
    let questionsData = [];
    let topicsData = [];
    let locationsData = [];
    await Promise.all([
      (async () => {
        topicsData = await fetchTopics();
        locationsData = await fetchLocations();
      })(),
    ]);

    // Fetch questions after we know the current location (default or from query string)
    // When a year is specified in the URL, include it to get SubQuestions for that year
    // If we have a specific questionId in the URL, fetch questions without location filter first
    // so the question metadata is available even if there's no data for that location
    const effectiveLocationAbbr = locationParam || (location ? location.LocationAbbr : "ALL");
    const effectiveYear = yearCodeParam || DEFAULT_YEAR;
    
    // If we have a questionId in URL, fetch questions without location filter to ensure we get the question
    if (questionCodeParam && effectiveLocationAbbr !== "ALL") {
      questionsData = await fetchQuestions({ filteredYear: effectiveYear });
    } else {
      questionsData = await fetchQuestions({ LocationAbbr: effectiveLocationAbbr, filteredYear: effectiveYear });
    }

    await setDataLoading(false);

    if (!questionsData || !locationsData || !questionsData) {
      setErrorLoadingData(true);
      setTopics([]);
      setLocations([]);
      setQuestions([]);
    } else {
      setErrorLoadingData(false);
      setTopics(topicsData);
      setLocations(locationsData);
      setQuestions(questionsData);

      // Seed dashboard filters from URL when present so returning to home preserves topic/subtopic
      if (topicIdParam) {
        try { await setTopicId(topicIdParam); } catch (e) {}
      }
      if (subTopicIdParam) {
        try { await setSubTopicId(subTopicIdParam); } catch (e) {}
      }

      //if we have a question code query string param
      if (questionCodeParam) {
        const question = questionsData.filter(
          (x) => x.QuestionId === questionCodeParam
        )[0];
        const location = locationsData.filter(
          (x) => x.LocationAbbr === locationParam
        )[0] || defaultLocation;

        // If the question wasn't found in the data, log warning but continue
        // The visualization components will handle showing "no data" appropriately
        if (!question) {
          console.warn(`Question ${questionCodeParam} not found for year ${yearCodeParam || DEFAULT_YEAR}`);
          // Still set the location and year so the page shows something
          if (location) setLocation(location);
          await setYear(yearCodeParam || DEFAULT_YEAR);
          return;
        }

        //temp code. this will get removed when routing is corrected
        let path = window.location.hash.replace("#/", "");
        path = path.substring(0, path.indexOf("/"));

        await setVizType(path);
        await setQuestion(question);
        await setQuestionId(questionCodeParam);
        await setSubQuestionValue(subQuestionCodeParam);
        await setYear(yearCodeParam || DEFAULT_YEAR);
        if (location) setLocation(location);

        const nextSelectedData = {
          vizType: path,
          question: question,
          questionId: question.QuestionId,
          subQuestionValue: subQuestionCodeParam,
          subQuestion: subQuestionCodeParam
            ? (question.SubQuestions || []).find((s) => String(s.SubQuestionId).toLowerCase() === String(subQuestionCodeParam).toLowerCase()) || null
            : null,
          year: yearCodeParam || DEFAULT_YEAR,
          location: location,
          scrollToTableHeader: false,
        };
        setSelectedData(nextSelectedData);
      }
    }
  }
  async function fetchLocations() {
    try {
      const locationResponse = await GetLocations();
      return locationResponse.data;
    } catch (error) {
      console.log(error);
    }
  }

  async function fetchQuestions({ filteredYear, LocationAbbr } = {}) {
    try {
      const questionResponse = await GetQuestions({ filteredYear, LocationAbbr });
      return questionResponse.data;
    } catch (error) {
      console.log(error);
    }
  }

  async function fetchTopics() {
    try {
      const topicResponse = await GetTopics();
      return topicResponse.data;
    } catch (error) {
      console.log(error);
    }
  }

  const displayLocationDropdown = () => {
    setDisplayLocations(!displayLocations);
  };

  // Persist sample size preferences whenever they change
  useEffect(() => {
    try {
      window.localStorage.setItem(
        "yrbs_showSampleSize",
        showSampleSize ? "true" : "false"
      );
    } catch (e) {}
  }, [showSampleSize]);

  async function handleHeaderhomeClick() {
    //analytics.selectTitleHeader();

    // Preserve location when returning to home - don't reset to defaultLocation
    await setVizType(null);
    await setQuestion(null);
    await setQuestionId(null);
    //await setSubTopicId(null);
    //await setTopicId(null);
    await setSubQuestionValue(null);
    await setYear(null);
    // Preserve topic/subtopic and location filters in URL so Dashboard restores selection
    const q = [];
    if (topicId) q.push(`topicId=${topicId}`);
    if (subTopicId) q.push(`subTopicId=${subTopicId}`);
    // Preserve location in URL if not ALL
    if (location && location.LocationAbbr && location.LocationAbbr !== "ALL") {
      q.push(`LocationAbbr=${location.LocationAbbr}`);
    }
    navigate(q.length > 0 ? `?${q.join("&")}` : ``);
    //window.location.href = ENV.ProfilesBaseURL;
  }
  function handleTopicChange(topicId) {
    if (topicId) {
      selectTopic({ topicId: topicId });
    }

    setSubTopicId(null);
    setTopicId(topicId);
    navigate(topicId && topicId.length > 0 ? `?topicId=${topicId}` : "");
  }

  const [subTopicId, setSubTopicId] = useState(null);
  function handleSubTopicChange(nextSubTopic) {
    const isObjectPayload = typeof nextSubTopic === "object" && nextSubTopic !== null;
    const nextTopicId = isObjectPayload ? nextSubTopic.topicId : null;
    const nextSubTopicId = isObjectPayload ? nextSubTopic.subTopicId : nextSubTopic;
    const effectiveTopicId = nextTopicId || topicId;

    if (nextTopicId && nextTopicId !== topicId) {
      setTopicId(nextTopicId);
    }

    setSubTopicId(nextSubTopicId);

    const q = [];
    if (effectiveTopicId) q.push(`topicId=${effectiveTopicId}`);
    if (nextSubTopicId && nextSubTopicId.length > 0) q.push(`subTopicId=${nextSubTopicId}`);
    navigate(q.length > 0 ? `?${q.join("&")}` : "");

    //TODO: ADOBE-ANALYTICS: subtopicid change
    if (nextSubTopicId && nextSubTopicId.length > 0) {
      selectSubTopic({ topicId: effectiveTopicId, subTopicId: nextSubTopicId });
    } else {
      selectSubTopicAll({ topicId: effectiveTopicId });
    }
  }
  function handleYearChange(year) {
    // year may be either { Year: 2020 } or { Year: 2020, Questions: [...] }
    if (!year) return;
    
    // Mark that we're handling a year change to prevent the URL hydration effect
    // from interfering with state updates.
    isYearChangeInProgress.current = true;
    
    const nextYear = year.Year;
    setYear(nextYear);

    // If the dropdown handler provided a question list payload, update app questions state
    if (year.Questions && Array.isArray(year.Questions)) {
      setQuestions(year.Questions);

      // If we currently have a selected question, try to replace it with the
      // same question from the newly-fetched list so its SubQuestions reflect
      // the selected year.
      if (questionId) {
        const newQuestion = year.Questions.find((q) => q.QuestionId === questionId);
        if (newQuestion) {
          // Determine an appropriate subQuestionValue for the refreshed question
          const availableSubQuestions = newQuestion.SubQuestions || [];
          // When changing years, the SubQuestionId may change (e.g., T_q9_1_SN94 in 2022 vs T_q10_1_SN94 in 2024)
          // even though they represent the same subtopic. We try to match by ID first, then by SubQuestion text.
          // This behavior is controlled by ENV.EnableSubQuestionTextMatching
          let nextSubQuestionValue = subQuestionValue;
          let nextSubQuestionObj = null;

          // Get the current subQuestion object from the old question's SubQuestions
          const currentSubQuestion = subQuestionValue && question && question.SubQuestions
            ? question.SubQuestions.find(
                (s) => String(s.SubQuestionId).toLowerCase() === String(subQuestionValue).toLowerCase()
              )
            : null;

          if (nextSubQuestionValue) {
            // First, try to find by exact ID (case-insensitive)
            nextSubQuestionObj = availableSubQuestions.find(
              (s) => String(s.SubQuestionId).toLowerCase() === String(nextSubQuestionValue).toLowerCase()
            ) || null;

            // If not found by ID and text matching is enabled, try to match by SubQuestion text
            if (!nextSubQuestionObj && ENV.EnableSubQuestionTextMatching && currentSubQuestion && currentSubQuestion.SubQuestion) {
              const matchByText = availableSubQuestions.find(
                (s) => String(s.SubQuestion).toLowerCase() === String(currentSubQuestion.SubQuestion).toLowerCase()
              );
              if (matchByText) {
                nextSubQuestionObj = matchByText;
                // Update the subQuestionValue to the new year's ID
                nextSubQuestionValue = matchByText.SubQuestionId;
              }
            }
          }

          // Update question and subquestion state so SubQuestions renders the new data
          setQuestion(newQuestion);
          setSubQuestionValue(nextSubQuestionValue);

          const nextSelectedData = {
            vizType: vizType,
            question: newQuestion,
            questionId: questionId,
            subQuestionValue: nextSubQuestionValue,
            subQuestion: nextSubQuestionObj,
            year: nextYear,
            location: location,
            // Don't scroll on year change - user didn't explicitly click a subquestion
            scrollToTableHeader: false,
          };
          setSelectedData(nextSelectedData);

          // Keep URL in sync with the newly-selected year so that the
          // hydration effect does not later overwrite the user's choice.
          if (vizType && vizType !== "null" && questionId && location) {
            const params = new URLSearchParams();
            params.set("questionId", questionId);
            params.set("LocationAbbr", location.LocationAbbr);
            if (nextSubQuestionValue) {
              params.set("subQuestionId", nextSubQuestionValue);
            }
            // params.set("year", String(nextYear));
            navigate(`${vizType}/?${params.toString()}`);
          }
          // Reset the flag after state updates are processed - use longer timeout to ensure
          // the hydration effect doesn't run before URL navigation completes
          setTimeout(() => { isYearChangeInProgress.current = false; }, 500);
          return;
        }
        // If matching question wasn't found in the new list, fall through and
        // just update selectedData.year below (preserve current question state).
      }
    }

    // Default behavior: update selectedData.year while preserving other selections
    const nextSelectedData = selectedData
      ? { ...selectedData, year: nextYear }
      : {
          vizType: vizType,
          question: question,
          questionId: questionId,
          subQuestionValue: subQuestionValue,
          year: nextYear,
          location: location,
          scrollToTableHeader: false,
        };
    setSelectedData(nextSelectedData);

    // Default path: update the URL query string with the selected year
    // while preserving the current viz, question, location, and subquestion.
    if (vizType && vizType !== "null" && questionId && location) {
      const params = new URLSearchParams();
      params.set("questionId", questionId);
      params.set("LocationAbbr", location.LocationAbbr);
      if (subQuestionValue) {
        params.set("subQuestionId", subQuestionValue);
      }
      // params.set("year", String(nextYear));
      navigate(`${vizType}/?${params.toString()}`);
    }
    
    // Reset the flag after state updates are processed - use longer timeout to ensure
    // the hydration effect doesn't run before URL navigation completes
    setTimeout(() => { isYearChangeInProgress.current = false; }, 500);
  }
  async function handleLocationChange(location, viz) {
    // Update location immediately
    await setLocation(location);
    await setSubQuestionValue(null);

    // Decide next viz type (if user picked location on dashboard without a question, we stay on dashboard: don't navigate)
    // Map view is only available for "All Locations" - fall back to table for individual locations
    let nextVizType = viz || vizType;
    if (nextVizType === "map" && location?.LocationAbbr !== "ALL") {
      nextVizType = "table";
    }

    // Determine a safe year: if we already have a selected year use it; else if there's a current question use its LatestYear; else null.
  const nextYear = year || DEFAULT_YEAR || (question ? question.LatestYear : null);

    // Build next selected data only if a question is currently selected; otherwise we leave selectedData as-is (dashboard state)
    if (question && questionId) {
      const nextSelectedData = {
        vizType: nextVizType,
        question: question,
        questionId: questionId,
        subQuestionValue: null,
        subQuestion: null,
        year: nextYear,
        location: location,
        scrollToTableHeader: false,
      };
      setSelectedData(nextSelectedData);
    }

    // If we are on the dashboard (no question selected), refetch questions for the selected location
    if (!question || !questionId) {
      try {
        const questionsData = await fetchQuestions({ LocationAbbr: location.LocationAbbr });
        setQuestions(questionsData || []);
      } catch (e) {
        console.log(e);
      }
    }

    // Only navigate if we have enough context (viz + question) to show a visualization; otherwise remain on dashboard
    if (question && questionId && nextVizType && nextVizType !== "null") {
    // const routeYear = nextYear || "";
  // const route = `${nextVizType}/?questionId=${questionId}&LocationAbbr=${location.LocationAbbr}${routeYear ? `&year=${routeYear}` : ""}`;
  const route = `${nextVizType}/?questionId=${questionId}&LocationAbbr=${location.LocationAbbr}`;
      navigate(route);
    } else {
      // Stay on dashboard, no viz route push.
      setVizType(viz || vizType); // keep existing vizType state stable
    }
  }

  //business logic for handleVizTypeChange
  //1. null passed in via one of the 4 objects below should clear the  value (most likely set to null)
  //2. if a value
  async function handleVizTypeChange({
    vizType,
    questionId,
    subQuestionId,
    locationAbbr,
    infoBannerMessage: bannerMsg,
  }) {
    // Set or clear info banner message
    // Only show banner when explicitly provided (e.g., from dashboard tile map click with location reset)
    // Clear it in all other navigation scenarios so it doesn't persist
    setInfoBannerMessage(bannerMsg || null);

    if (questionId) {
      const question = questions.filter((x) => x.QuestionId === questionId)[0];

      //NEED TO FIX 'RESET' Logic
      const nextSubQuestionValue =
        subQuestionId === null || subQuestionId
          ? subQuestionId
          : subQuestionValue;

  const nextYear = year || DEFAULT_YEAR || question.LatestYear;

      const nextLocationAbbr = locationAbbr || location.LocationAbbr;
      const nextLocation = locations.filter(
        (x) => x.LocationAbbr === nextLocationAbbr
      )[0];

      const subQuestion = nextSubQuestionValue
        ? question.SubQuestions.find(
            (x) => String(x.SubQuestionId).toLowerCase() === String(nextSubQuestionValue).toLowerCase()
          ) || null
        : null;
      const nextSelectedData = {
        vizType: vizType,
        question: question,
        questionId: questionId,
        subQuestionValue: nextSubQuestionValue,
        subQuestion: subQuestion,
        year: nextYear,
        location: nextLocation,
        // Only scroll if user is selecting a NEW subquestion (different from current)
        scrollToTableHeader: nextSubQuestionValue && nextSubQuestionValue !== subQuestionValue,
      };

      await setLocation(nextLocation);
      await setVizType(vizType);
      await setQuestion(question);
      await setQuestionId(questionId);
      await setSubQuestionValue(nextSubQuestionValue);
      await setYear(nextYear);

      await setSelectedData(nextSelectedData);

      if (nextSubQuestionValue) {
        // const nextUrl = `${vizType}/?questionId=${
        //   question.QuestionId
        // }&LocationAbbr=${
        //   locationAbbr || location.LocationAbbr
        // }&subQuestionId=${subQuestionId}&year=${nextYear}`;
        const nextUrl = `${vizType}/?questionId=${question.QuestionId}&LocationAbbr=${nextLocationAbbr}&subQuestionId=${subQuestionId}`;
        navigate(nextUrl);
      } else {
        // const nextnavigate = `${vizType}/?questionId=${questionId}${
        //   nextSubQuestionValue ? "&subQuestionId=" + nextSubQuestionValue : ""
        // }&LocationAbbr=${location.LocationAbbr}&year=${nextYear}`;
        const nextnavigate = `${vizType}/?questionId=${questionId}${nextSubQuestionValue ? "&subQuestionId=" + nextSubQuestionValue : ""}&LocationAbbr=${nextLocationAbbr}`;
        navigate(nextnavigate);
      }
    } else {
      // Return to dashboard - reset question-related state but preserve location
      await setVizType(null);
      await setQuestion(null);
      await setQuestionId(null);
      await setSubQuestionValue(null);
      await setYear(null);
      await setSelectedData(null);
      // Preserve location when returning to home - don't reset to defaultLocation
      const q = [];
      if (topicId) q.push(`topicId=${topicId}`);
      if (subTopicId) q.push(`subTopicId=${subTopicId}`);
      // Preserve location in URL if not ALL
      if (location && location.LocationAbbr && location.LocationAbbr !== "ALL") {
        q.push(`LocationAbbr=${location.LocationAbbr}`);
      }
      // Navigate to root path (/) to clear the viz route (table/graph/map)
      // This prevents white screen issues when the URL keeps the viz path
      navigate(q.length > 0 ? `/?${q.join("&")}` : `/`);
    }
  }

  function handleCDCHeaderClick() {
    selectCdcHeader();
  }

  function handleHeaderHelpClick() {
    selectCdcHelpLink();
  }

  //make sure both have loaded as both are needed
  return (
    <div className="App" id="App" style={{ position: "relative", zIndex: 0 }}>
      {/* Loading overlay for year change and other data fetches */}
      <LoadingOverlay visible={dataLoading} text="Loading…" />
      <CDCHeader search={false} title={'School Health Profiles Explorer'}></CDCHeader>
      <div id="nav-header">
        <div role="navigation" className="container" id="top-nav-row">
          <nav className="navbar flex-md-nowrap p-0  " style={{}}>
            <button
              className="btn btn-link"
              style={{}}
              onClick={handleHeaderhomeClick}
            >
              School Health Profiles Explorer
            </button>
            <ul className="navbar-nav px-3">
              <li className="nav-item text-nowrap help-link">
               <a href={ENV.CDCBaseURL + "/school-health-profiles/profiles-explorer/index.html"} target="_blank" rel="noreferrer" >
                    <span className="cdc-icon-question-circle-solid"></span> Help
                    Using School Health Profiles Explorer
                </a>
              </li>
            </ul>
          </nav>
        </div>
        <div
          style={{ backgroundColor: "rgba(245, 245, 245, 1)" }}
          id="bottom-nav-row"
        >
          <nav
            role="navigation"
            aria-label="Breadcrumb"
            className="breadcrumbs pl-3 container"
          >
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href={ENV.CDCBaseURL}>CDC</a>
              </li>
              <li className="breadcrumb-item">
                <a href={ENV.CDCBaseURL + "/healthy-youth/index.html"}>
                  DASH Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a href={ENV.CDCBaseURL + "/healthy-youth/data-statistics/index.html"}>
                  Data & Statistics Home
                </a>
              </li>
              <li className="breadcrumb-item">
                <a
                  href={ENV.CDCBaseURL + "/school-health-profiles/index.html"}
                >
                  School Health Profiles
                </a>
              </li>
            </ol>

            <ul id="help-text" className="navbar-nav px-3">
              <li className="nav-item text-nowrap help-link">
                <a
                  href={ENV.CDCBaseURL + "/school-health-profiles/profiles-explorer/index.html"}
                  target="_blank"
                  rel="noreferrer"
                  onClick={handleHeaderHelpClick}
                >
                    <span className="cdc-icon-question-circle-solid"></span> Help
                    Using School Health Profiles Explorer
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <CDCBody>
        {errorLoadingData && (
          <div className="alert alert-danger" role="alert">
            Error loading data. Please refresh page.
          </div>
        )}
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                onVizTypeChange={handleVizTypeChange}
                onTopicChange={handleTopicChange}
                topicId={topicId}
                onSubTopicChange={handleSubTopicChange}
                subTopicId={subTopicId}
                questionId={questionCodeParam}
                topics={topics}
                questions={questions}
                // location filter props
                location={location}
                locations={locations}
                displayLocations={displayLocations}
                displayLocationDropdown={displayLocationDropdown}
                onLocationChange={handleLocationChange}
                setLocationSelectedFromDropdownMenu={setLocationSelectedFromDropdownMenu}
              ></Dashboard>
            }
          />
          <Route
            path={"/" + vizType}
            element={
              selectedData && selectedData.question ? (
                <DataVisualization
                  selectedData={selectedData}
                  onVizTypeChange={handleVizTypeChange}
                  setVizType={setVizType}
                  displayLocationDropdown={displayLocationDropdown}
                  displayLocations={displayLocations}
                  onLocationChange={handleLocationChange}
                  setLocationSelectedFromDropdownMenu={
                    setLocationSelectedFromDropdownMenu
                  }
                  subQuestionValue={subQuestionValue}
                  onYearChange={handleYearChange}
                  locations={locations}
                  showSampleSize={showSampleSize}
                  setShowSampleSize={setShowSampleSize}
                  setDataLoading={setDataLoading}
                  infoBannerMessage={infoBannerMessage}
                  setInfoBannerMessage={setInfoBannerMessage}
                />
              ) : null
            }
          />
        </Routes>

        <ScrollToTop />
      </CDCBody>
      <CDCFooter></CDCFooter>
    </div>
  );
};

export default App;
